// src/services/premisesService.js
import { apiRequest } from './api';

export const premisesService = {
    // Get premises list based on user role
    // Get premises list based on user role
    getPremises: async (params = {}) => {
        try {
            const response = await apiRequest.get('/premises', { params });
            // Extract the nested data
            return response.data.data.data; // { premises: [...], pagination: {...} }
        } catch (error) {
            throw new Error(error.message || 'Failed to fetch premises');
        }
    },

    // Get every premises row across paginated API responses (for dropdown/filter use-cases)
    getPremisesComplete: async (params = {}, useAllEndpoint = false) => {
        const pageSize = 100;
        let page = 1;
        let totalPages = 1;
        const combined = [];

        do {
            const endpoint = useAllEndpoint ? '/premises/all' : '/premises';
            const response = await apiRequest.get(endpoint, {
                params: {
                    ...params,
                    page,
                    limit: pageSize
                }
            });

            const payload = response?.data?.data?.data || {};
            const rows = Array.isArray(payload.premises) ? payload.premises : [];
            const pagination = payload.pagination || {};
            totalPages = Number(pagination.total_pages || 1);
            combined.push(...rows);
            page += 1;
        } while (page <= totalPages);

        return {
            premises: combined,
            pagination: {
                current_page: 1,
                per_page: combined.length,
                total: combined.length,
                total_pages: 1
            }
        };
    },

    // Get all premises (Core Admin only)
    getAllPremises: async (params = {}) => {
        try {
            const response = await apiRequest.get('/premises/all', {
                params: {
                    page: params.page || 1,
                    limit: params.limit || 20,
                    search: params.search || '',
                    organization_id: params.organization_id || '',
                    status: params.status || ''
                }
            });
            // Same unwrapping
            return response.data.data.data; // { premises: [...], pagination: {...} }
        } catch (error) {
            throw new Error(error.message || 'Failed to fetch all premises');
        }
    },

    // Get single premises details
    getPremisesById: async (premisesId) => {
        try {
            const response = await apiRequest.get(`/premises/${premisesId}`);
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to fetch premises details');
        }
    },

    // Create new premises (role-based)
    createPremises: async (premisesData, hasFile = false) => {
        try {
            const config = hasFile
                ? { headers: { 'Content-Type': 'multipart/form-data' } }
                : {};
            const response = await apiRequest.post('/premises', premisesData, config);
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to create premises');
        }
    },

    updatePremises: async (premisesId, premisesData, hasFile = false) => {
        try {
            const config = hasFile
                ? { headers: { 'Content-Type': 'multipart/form-data' } }
                : {};

            // ALWAYS use PUT /premises/{id} — your PHP handles permissions internally
            const response = await apiRequest.put(
                `/premises/${premisesId}`,
                premisesData,
                config
            );
            console.log(premisesData);
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to update premises');
        }
    },
    // Update only the premises logo
    updatePremisesLogo: async (premisesId, logoFile, remove = false) => {
        try {
            const formData = new FormData();
            if (remove) {
                formData.append('remove', '1');
            } else if (logoFile) {
                formData.append('logo', logoFile);
            } else {
                throw new Error('No logo provided');
            }

            const response = await apiRequest.post(`/premises/${premisesId}/logo`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to update premises logo');
        }
    },
    // Update premises (role-based)
    /*updatePremises: async (premisesId, premisesData) => {
        try {
            // For core admin, use the organization premises endpoint
            const isAdmin = localStorage.getItem('user_type') === 'core_admin';
            if (isAdmin) {
                const response = await apiRequest.put(`/organizations/premises?premises_id=${premisesId}`, premisesData);
                return response.data.data;
            } else {
                const response = await apiRequest.put(`/premises/${premisesId}`, premisesData);
                return response.data.data;
            }
        } catch (error) {
            throw new Error(error.message || 'Failed to update premises');
        }
    },*/

    // Delete premises (Core Admin only)
    deletePremises: async (premisesId) => {
        try {
            const response = await apiRequest.delete(`/organizations/premises?premises_id=${premisesId}`);
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to delete premises');
        }
    },

    // Update premises status (Core Admin and Client Admin)
    updatePremisesStatus: async (premisesId, status) => {
        try {
            const response = await apiRequest.put('/premises/status', {
                premises_id: premisesId,
                status: status
            });
            return { ...response.data.data, message: response.data.message };
        } catch (error) {
            throw new Error(error.message || 'Failed to update premises status');
        }
    },

    // Get premises statistics
    getPremisesStats: async () => {
        try {
            const response = await apiRequest.get('/premises/stats');
            return response.data.data; // ✅ Wait — no!
        } catch (error) {
            throw new Error(error.message || 'Failed to fetch premises statistics');
        }
    },

    // Log geofence event
    logGeofenceEvent: async (eventData) => {
        try {
            const response = await apiRequest.post('/geofence', eventData);
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to log geofence event');
        }
    },

    // Get geofence logs
    getGeofenceLogs: async (params = {}) => {
        try {
            const response = await apiRequest.get('/geofence', { params });
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to fetch geofence logs');
        }
    },

    // Get premises types for organization category
    getPremisesTypes: async (orgCategoryId = '') => {
        try {
            const params = orgCategoryId ? { org_category_id: orgCategoryId } : {};
            const response = await apiRequest.get('/premises-types', { params });
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to fetch premises types');
        }
    },

    // Get staff for premises (for all user roles)
    getPremisesStaff: async (premisesId, params = {}) => {
        try {
            const response = await apiRequest.get('/staff', {
                params: { ...params, premises_id: premisesId }
            });
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to fetch premises staff');
        }
    },

    // Get attendance for premises (for all user roles)
    getPremisesAttendance: async (premisesId, params = {}) => {
        try {
            const response = await apiRequest.get('/attendance', {
                params: { ...params, premises_id: premisesId }
            });
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to fetch premises attendance');
        }
    },

    // Get leave requests for premises (for all user roles)
    getPremisesLeaves: async (premisesId, params = {}) => {
        try {
            const response = await apiRequest.get('/leaves', {
                params: { ...params, premises_id: premisesId }
            });
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to fetch premises leave requests');
        }
    },

    // Bulk upload staff for premises (for all user roles)
    bulkUploadPremisesStaff: async (orgId, premisesId, fileData, imageFiles = [], uploadMethod = 'separate') => {
        try {
            const formData = new FormData();
            formData.append('org_id', orgId);
            formData.append('premises_id', premisesId);
            formData.append('upload_method', uploadMethod);

            if (uploadMethod === 'zip') {
                formData.append('zip_file', fileData);
            } else {
                formData.append('csv_file', fileData);
                imageFiles.forEach((file) => {
                    formData.append('image_files[]', file);
                });
            }

            const response = await apiRequest.post('/staff/bulk', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to upload staff bulk data');
        }
    },

    // Export premises data
    exportPremises: async (format = 'csv', filters = {}) => {
        try {
            const params = { ...filters, format: 'csv' };
            const response = await apiRequest.get('/premises/export', {
                params,
                responseType: 'blob'
            });

            // Create download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `premises_export_${new Date().toISOString().split('T')[0]}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            throw new Error(error.message || 'Failed to export premises data');
        }
    },

    // Get premises reports
    getPremisesReports: async (premisesId, reportType, params = {}) => {
        try {
            const response = await apiRequest.get(`/premises/${premisesId}/reports/${reportType}`, { params });
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to fetch premises reports');
        }
    }
};
